import { RenderedFormProps } from "../RenderForm.tsx";
import { capitalizeFirst, displayDateAndTime } from "../../../helper-functions";
import { RadioGroup, RadioOption } from "../../../component-library";
import { Col, Container, Form, Row, Table } from "react-bootstrap";
import {
    useAccountQuery,
    useShipmentQuery,
    useShippingMethodQuery,
    useTissueQuery,
    useUserBasicQuery,
} from "../../../queries";
import { QueryType, YesNoRadioOptions } from "../../../models";

const yesNoOptions = YesNoRadioOptions.map<RadioOption>((o) => ({ ...o, value: capitalizeFirst(o.value) }));

export const TissueOmarSatelliteTransfer = ({ formState }: Readonly<RenderedFormProps>) => {
    const {
        detailQuery: { data: tissue },
    } = useTissueQuery({ queryType: QueryType.Detail, id: formState.formData?.tissueId });

    const {
        detailQuery: { data: shipment },
    } = useShipmentQuery({ queryType: QueryType.Detail, id: formState.formData?.shipmentId });

    const userIds: number[] = [shipment?.createdById, shipment?.packedByUserId].filter((id): id is number => !!id);

    const {
        listQuery: { data: users },
    } = useUserBasicQuery({ ids: userIds });

    const accountIds: number[] = [shipment?.fromAccountId, shipment?.toAccountId].filter((id): id is number => !!id);

    const {
        getByIdsQuery: { data: accounts },
    } = useAccountQuery({ ids: accountIds });

    return (
        <Container className="p-4">
            <div className="text-center mb-4">
                <h1>Ocular Management Record - Satellite Transfer</h1>
            </div>
            <div className="mb-4">
                <Row>
                    <Col>Tissue Number: {tissue?.number}</Col>
                    <Col>Transfer Initiated by: {users?.find((u) => u.id === shipment?.createdById)?.name}</Col>
                    <Col>{displayDateAndTime(shipment?.createdOn)}</Col>
                </Row>
            </div>

            <Form className="mb-4">
                <h2>Notification of Satellite Transfer:</h2>
                <Row>
                    <Col sm={6} className="ps-0">
                        Originating Location: {accounts?.find((a) => a?.id === shipment?.fromAccountId)?.name}
                    </Col>
                    <Col>Shipment Method: {shipment?.shippingMethod?.name}</Col>
                </Row>
                <Row>
                    <Col sm={6} className="ps-0">
                        Transfer Location: {accounts?.find((a) => a?.id === shipment?.toAccountId)?.name}
                    </Col>
                    <Col>Tracking Number: {shipment?.trackingNumber}</Col>
                    <Col>Tissue Packed By: {users?.find((u) => u.id === shipment?.packedByUserId)?.name}</Col>
                </Row>
            </Form>

            <Table bordered>
                <thead>
                    <tr>
                        <th className="col-6">Assessment of Incoming Tissue</th>
                        <th className="text-center">OD</th>
                        <th className="text-center">OS</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td className="col-6">
                            Tissue number on label is consistent with Donor information/Return information
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueNumberConsistentWithDonorInfoOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueNumberConsistentWithDonorInfoOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Indication of wet-ice upon arrival</td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="indicationOfWetIceUponArrivalOD" />
                        </td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="indicationOfWetIceUponArrivalOS" />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Storage solution acceptable (if Optisol, color range acceptable)</td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="preservationMediumAcceptableOD" />
                        </td>
                        <td className="text-center">
                            <RadioGroup inline options={yesNoOptions} accessor="preservationMediumAcceptableOS" />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Shipping container: Secured and absent of leaks.</td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="shippingContainerSecuredAndAbsentOfLeaksOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="shippingContainerSecuredAndAbsentOfLeaksOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">
                            Tissue storage container: Absent of leaks and tamper-evident seals intact (if applicable)
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueStorageContainerAbsentOfLeaksAndTamperEvidentSealsIntactOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueStorageContainerAbsentOfLeaksAndTamperEvidentSealsIntactOS"
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="col-6">Tissue was transported under Quarantine (if applicable)</td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueWasTransportedUnderQuarantineOD"
                            />
                        </td>
                        <td className="text-center">
                            <RadioGroup
                                inline
                                options={yesNoOptions}
                                accessor="tissueWasTransportedUnderQuarantineOS"
                            />
                        </td>
                    </tr>
                </tbody>
            </Table>
        </Container>
    );
};
